import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../../shared/contexts/NotificationContext';
import type { CartItem } from '../../shared/types';
import { getApiEndpoint } from '../../shared/utils/api-url';
import { buscarCEP, calcularDistanciaCEP, calcularFrete, formatCEP } from '../../shared/utils/cep';
import { enviarMensagemWhatsApp, obterNumeroWhatsApp, type OrderData as WhatsAppOrderData } from '../../shared/utils/whatsapp';

function Checkout() {
  const navigate = useNavigate();
  const { success, error } = useNotification();
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [subtotal, setSubtotal] = useState(0);
  const [shippingCost, setShippingCost] = useState(0);
  const [loadingCEP, setLoadingCEP] = useState(false);
  const [installments, setInstallments] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    cep: '',
    address: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    paymentMethod: 'pix',
  });
  
  // CEP de origem da loja (configurar conforme necessário)
  const CEP_ORIGEM = '01310-100'; // Exemplo: Avenida Paulista, São Paulo

  useEffect(() => {
    loadCart();
  }, []);

  useEffect(() => {
    calculateTotals();
  }, [cartItems]);

  // Recalcular frete quando o subtotal mudar (para aplicar frete grátis)
  useEffect(() => {
    if (formData.cep && formData.cep.replace(/\D/g, '').length === 8) {
      calcularFretePorCEP(formData.cep);
    }
  }, [subtotal]);

  const loadCart = async () => {
    setLoading(true);
    try {
      const savedCart = localStorage.getItem('cart');
      if (savedCart) {
        setCartItems(JSON.parse(savedCart));
      } else {
        setCartItems([]);
      }
    } catch (error) {
      console.error('Erro ao carregar carrinho:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = () => {
    const sub = cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
    setSubtotal(sub);
    // O frete será calculado quando o CEP for preenchido
  };

  // Calcular frete baseado no CEP usando coordenadas geográficas
  const calcularFretePorCEP = async (cep: string) => {
    if (!cep || cep.replace(/\D/g, '').length !== 8) {
      setShippingCost(0);
      return;
    }

    try {
      // Calcular distância usando coordenadas geográficas (Haversine)
      const distancia = await calcularDistanciaCEP(CEP_ORIGEM, cep);
      
      if (distancia === 0) {
        // Se não conseguir calcular, usar valor padrão
        const sub = cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
        setShippingCost(sub >= 200 ? 0 : 15);
        return;
      }

      const frete = calcularFrete(distancia);
      
      // Frete grátis para compras acima de R$ 200
      const sub = cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
      setShippingCost(sub >= 200 ? 0 : frete);
    } catch (error) {
      console.error('Erro ao calcular frete:', error);
      // Valor padrão caso haja erro
      const sub = cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
      setShippingCost(sub >= 200 ? 0 : 15);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Se o campo for CEP, formatar e buscar dados
    if (name === 'cep') {
      const cepLimpo = value.replace(/\D/g, '');
      const cepFormatado = formatCEP(cepLimpo);
      setFormData(prev => ({ ...prev, cep: cepFormatado }));
      
      // Buscar CEP quando tiver 8 dígitos
      if (cepLimpo.length === 8) {
        buscarDadosCEP(cepFormatado);
      } else {
        // Resetar frete se CEP incompleto
        setShippingCost(0);
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Buscar dados do CEP e preencher formulário
  const buscarDadosCEP = async (cep: string) => {
    setLoadingCEP(true);
    try {
      const dados = await buscarCEP(cep);
      
      if (dados) {
        setFormData(prev => ({
          ...prev,
          address: dados.logradouro || prev.address,
          neighborhood: dados.bairro || prev.neighborhood,
          city: dados.localidade || prev.city,
          state: dados.uf || prev.state,
        }));
        
        // Calcular frete
        await calcularFretePorCEP(cep);
      } else {
        // Se não encontrar, calcular frete mesmo assim
        await calcularFretePorCEP(cep);
      }
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
    } finally {
      setLoadingCEP(false);
    }
  };

  const handlePaymentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, paymentMethod: e.target.value }));
    // Resetar parcelas quando mudar forma de pagamento
    if (e.target.value !== 'credit') {
      setInstallments(1);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validação básica
    if (!formData.name || !formData.phone || !formData.address || !formData.cep) {
      error('Por favor, preencha todos os campos obrigatórios');
      return;
    }

    if (cartItems.length === 0) {
      error('Seu carrinho está vazio');
      return;
    }

    setSubmitting(true);

    try {
      // Preparar dados do pedido
      const orderItems = cartItems.map(item => ({
        id: item.product_id || item.id,
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        color: item.color || null,
        size: item.size || null,
      }));

      // Montar endereço completo
      const fullAddress = `${formData.address}, ${formData.number}${formData.complement ? ' - ' + formData.complement : ''}, ${formData.neighborhood}, ${formData.city}${formData.state ? ' - ' + formData.state : ''}, CEP: ${formData.cep}`;

      const orderData = {
        customer_name: formData.name,
        customer_address: fullAddress,
        customer_phone: formData.phone,
        payment_method: formData.paymentMethod,
        payment_type: formData.paymentMethod,
        installments: formData.paymentMethod === 'credit' ? installments.toString() : '1',
        items: orderItems,
        subtotal: subtotal,
        shipping_cost: shippingCost,
        total: subtotal + shippingCost,
      };

      // Criar pedido na API
      const response = await fetch(getApiEndpoint('/orders'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erro ao criar pedido');
      }

      const result = await response.json();
      
      // Preparar dados para WhatsApp
      const whatsappOrderData: WhatsAppOrderData = {
        orderCode: result.order_code,
        customerName: formData.name,
        customerPhone: formData.phone,
        customerAddress: fullAddress,
        paymentMethod: formData.paymentMethod,
        installments: formData.paymentMethod === 'credit' ? installments : undefined,
        items: orderItems.map(item => ({
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          color: item.color || undefined,
          size: item.size || undefined,
        })),
        subtotal: subtotal,
        shippingCost: shippingCost,
        total: subtotal + shippingCost,
      };
      
      // Limpar carrinho
      localStorage.removeItem('cart');
      
      // Salvar código do pedido pendente para mostrar confirmação
      localStorage.setItem('pending_order_code', result.order_code);
      localStorage.setItem('pending_order_id', result.id.toString());
      // Salvar timestamp de criação para calcular timeout
      localStorage.setItem('pending_order_created_at', Date.now().toString());
      
      success(`Pedido ${result.order_code} criado com sucesso! Abrindo WhatsApp...`);
      
      // Obter número do WhatsApp e enviar mensagem
      try {
        const whatsappNumber = await obterNumeroWhatsApp();
        enviarMensagemWhatsApp(whatsappOrderData, whatsappNumber);
      } catch (error) {
        console.error('Erro ao enviar mensagem para WhatsApp:', error);
        // Continuar mesmo se houver erro no WhatsApp
      }
      
      // Redirecionar para home após 2 segundos (dar tempo para abrir WhatsApp)
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (err: any) {
      console.error('Erro ao finalizar pedido:', err);
      error(err.message || 'Erro ao finalizar pedido. Tente novamente.');
      setSubmitting(false);
    }
  };

  const formatPrice = (price: number) => {
    return `R$ ${price.toFixed(2).replace('.', ',')}`;
  };

  const total = subtotal + shippingCost;

  if (loading) {
    return (
      <div className="mobile-content">
        <div className="loading-message">Carregando...</div>
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div className="mobile-content">
        <div className="empty-message">
          <p>Seu carrinho está vazio</p>
          <button onClick={() => navigate('/catalogo')} className="btn btn-primary">
            Ver Produtos
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-content">
      {/* Steps Indicator */}
      <section className="steps-section">
        <div className="step active">
          <div className="step-number">1</div>
          <span className="step-label">Itens</span>
        </div>
        <div className="step-line"></div>
        <div className="step active">
          <div className="step-number">2</div>
          <span className="step-label">Dados</span>
        </div>
        <div className="step-line"></div>
        <div className="step">
          <div className="step-number">3</div>
          <span className="step-label">Pagamento</span>
        </div>
      </section>

      {/* Order Summary */}
      <section className="order-summary">
        <h2 className="section-title">Resumo do Pedido</h2>
        {cartItems.map((item) => (
          <div key={item.id} className="summary-item">
            <span className="item-name">{item.name} x{item.quantity}</span>
            <span className="item-price">{formatPrice(item.price * item.quantity)}</span>
          </div>
        ))}
        <div className="summary-row">
          <span className="summary-label">Subtotal</span>
          <span className="summary-value">{formatPrice(subtotal)}</span>
        </div>
        <div className="summary-row">
          <span className="summary-label">Frete</span>
          <span className="summary-value">
            {shippingCost === 0 ? 'Grátis' : formatPrice(shippingCost)}
          </span>
        </div>
        <div className="summary-total">
          <span className="summary-label">Total</span>
          <span className="summary-value">{formatPrice(total)}</span>
        </div>
      </section>

      {/* Customer Info Form */}
      <section className="form-section">
        <h2 className="section-title">Dados do Cliente</h2>
        <form className="checkout-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Nome Completo *</label>
            <input
              type="text"
              className="form-input"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Digite seu nome"
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">Telefone *</label>
            <input
              type="tel"
              className="form-input"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="(00) 00000-0000"
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">CEP *</label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                className="form-input"
                name="cep"
                value={formData.cep}
                onChange={handleInputChange}
                placeholder="00000-000"
                maxLength={9}
                required
              />
              {loadingCEP && (
                <span style={{ 
                  position: 'absolute', 
                  right: '10px', 
                  top: '50%', 
                  transform: 'translateY(-50%)' 
                }}>
                  <i className="fas fa-spinner fa-spin"></i>
                </span>
              )}
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Endereço *</label>
              <input
                type="text"
                className="form-input"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="Rua, Avenida..."
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Número *</label>
              <input
                type="text"
                className="form-input"
                name="number"
                value={formData.number}
                onChange={handleInputChange}
                placeholder="123"
                required
              />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Complemento</label>
            <input
              type="text"
              className="form-input"
              name="complement"
              value={formData.complement}
              onChange={handleInputChange}
              placeholder="Apto, Casa..."
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Bairro *</label>
              <input
                type="text"
                className="form-input"
                name="neighborhood"
                value={formData.neighborhood}
                onChange={handleInputChange}
                placeholder="Bairro"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Cidade *</label>
              <input
                type="text"
                className="form-input"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                placeholder="Cidade"
                required
              />
            </div>
          </div>

          {/* Payment Section */}
          <div className="payment-section">
            <h2 className="section-title">Forma de Pagamento</h2>
            <div className="payment-options">
              <label className="payment-option">
                <input
                  type="radio"
                  name="payment"
                  value="pix"
                  checked={formData.paymentMethod === 'pix'}
                  onChange={handlePaymentChange}
                />
                <span className="payment-label">
                  <i className="fas fa-qrcode"></i>
                  PIX
                </span>
              </label>
              <label className="payment-option">
                <input
                  type="radio"
                  name="payment"
                  value="credit"
                  checked={formData.paymentMethod === 'credit'}
                  onChange={handlePaymentChange}
                />
                <span className="payment-label">
                  <i className="fas fa-credit-card"></i>
                  Cartão de Crédito
                </span>
              </label>
              {formData.paymentMethod === 'credit' && (
                <div className="installments-selector" style={{ 
                  marginTop: '10px', 
                  padding: '10px', 
                  backgroundColor: '#f5f5f5', 
                  borderRadius: '8px' 
                }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                    Parcelamento:
                  </label>
                  <select
                    value={installments}
                    onChange={(e) => setInstallments(parseInt(e.target.value))}
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #ddd',
                      fontSize: '14px'
                    }}
                  >
                    <option value={1}>1x de {formatPrice(total)} sem juros</option>
                    <option value={2}>2x de {formatPrice(total / 2)} sem juros</option>
                    <option value={3}>3x de {formatPrice(total / 3)} sem juros</option>
                  </select>
                  <p style={{ 
                    marginTop: '5px', 
                    fontSize: '12px', 
                    color: '#666' 
                  }}>
                    Parcelamento em até 3x sem juros
                  </p>
                </div>
              )}
              <label className="payment-option">
                <input
                  type="radio"
                  name="payment"
                  value="boleto"
                  checked={formData.paymentMethod === 'boleto'}
                  onChange={handlePaymentChange}
                />
                <span className="payment-label">
                  <i className="fas fa-barcode"></i>
                  Boleto
                </span>
              </label>
            </div>
          </div>

          {/* Submit Button */}
          <div className="submit-section">
            <button 
              type="submit" 
              className="submit-btn"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Processando...
                </>
              ) : (
                <>
                  Finalizar Pedido
                  <i className="fas fa-arrow-right"></i>
                </>
              )}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

export default Checkout;

